import { UserAvatarBadge } from 'app/components/user-avatar-badge';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { ListingsSortBy, ListingType } from 'app/types/listing';
import type { useLandingPage } from './use-landing-page';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import { navigateToAllListings, navigateToMyInformation, navigateToUserProfile } from '../navigator-helper';
import { navigateToAllChannels } from 'app/screens/chat/navigator-helper';
import { GreetingSkeleton } from './components/greeting-skeleton';
import {
  QUICK_ACTION_CARD_HEIGHT,
  QUICK_ACTION_CARD_MAX_HEIGHT,
  QuickActionCard,
} from './components/quick-action-card';
import { QuickActionsSkeleton } from './components/quick-actions-skeleton';
import { useUISize } from 'app/utils/web-helpers';
import { LoanType, isLoanPendingCurrentUserRating } from 'app/types/loan';
import { type Interest } from 'app/types/interest';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { canUseInterests, FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { userInfoAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import { useTrackStreakFlameClick } from 'app/lib/streak-analytics';
import { FlatList } from 'react-native-gesture-handler';
import { LoanFilterKey, useAllLoans } from 'app/network/loan';
import { useMonthlyStatsAll } from 'app/network/monthly-stats';
import { dtoToStatus } from 'app/screens/profile/streak/types';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { useHasPartagePlusSubscription } from 'app/hooks/use-has-partage-plus-subscription';
import { getLoansWithoutPosts } from 'app/network/post';
import { useNewListingPrompt } from 'app/hooks/use-new-listing-prompt';
import type { TFunction } from 'i18next';
import { useUser } from 'app/network/user';
import { navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { useEditProfileAvatar } from 'app/components/profile/my-information/avatar-section';
import { StepsToComplete } from 'app/components/steps-to-complete';
import { openLandingSubscription } from './subscription-navigation';
const theme = createDSTheme();

type LandingNavigation = ReturnType<typeof useLandingPage>['navigation'];
type LandingProfile = ReturnType<typeof useLandingPage>['profile'];
type LandingUser = ReturnType<typeof useLandingPage>['user'];
type LandingTagId = Interest['tagId'];
type QuickActionElement = React.ReactElement<{ style?: StyleProp<ViewStyle> }>;

export function useLandingPageNavigationActions(navigation: LandingNavigation) {
  const isSmallUI = useUISize().isSmallUI;
  const { data: user, isPending: isUserPending } = useUser();
  return {
    openExploreOffers: () => navigateToAllListings(navigation, { type: ListingType.offer }),
    openRecentOffers: () =>
      navigateToAllListings(navigation, { type: ListingType.offer, search: '', sort: ListingsSortBy.Date }),
    openFavourites: () => {
      navigation.navigate('management', { screen: 'management-overview' });
      setTimeout(() => navigation.navigate('management', { screen: 'favourites' }), 10);
    },
    openMessages: () => navigateToAllChannels(navigation),
    openMyInformation: () => navigateToMyInformation(navigation),
    openMyPlan: () => openLandingSubscription(navigation, isSmallUI, 'my-plan'),
    openPayment: () => openLandingSubscription(navigation, isSmallUI, 'payment'),
    openOfferTag: (tagId: LandingTagId) => navigateToAllListings(navigation, { tagId, type: ListingType.offer }),
    openOfferCategory: (categoryId: string) =>
      navigateToAllListings(navigation, { categoryId, type: ListingType.offer }),
    openRequests: () => navigateToAllListings(navigation, { type: ListingType.request }),
    openManagementOverview: () => {
      if (isSmallUI) {
        navigation.navigate('management', { screen: 'management-overview' });
      } else {
        navigation.navigate('management', { screen: 'my-loans', params: { type: LoanType.Lending } });
      }
    },
    openPendingRatings: (type?: LoanType) =>
      navigation.navigate('management', {
        screen: 'past-loans',
        params: { ratingFilter: 'unrated', type },
        initial: false,
      }),
    openSharerInterests: () => navigation.navigate('profile', { screen: 'my-interests' }),
    openStreak: () => navigation.navigate('streak', undefined, { pop: true }),
    openPublishFlow: () => {
      navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending);
    },
  };
}

export { useHasPartagePlusSubscription } from 'app/hooks/use-has-partage-plus-subscription';

export function useProfileCompletionSteps({
  hasCustomProfilePicture,
  profile,
  setCompleteProfilePromptCompleted,
  user,
  actions,
  userInterests,
}: {
  hasCustomProfilePicture: boolean;
  profile: LandingProfile;
  setCompleteProfilePromptCompleted: (completed: boolean) => void;
  user: LandingUser;
  actions: ReturnType<typeof useLandingPageNavigationActions>;
  userInterests: Interest[] | undefined;
}) {
  const { t } = useTranslation('landing-page');
  const { onUseImagePickerCamera, settingsPromptState, isPending, isPickingImage } = useEditProfileAvatar(profile);
  // @TODO : Maybe compute the completed boolean directely on the client-side instead of adding it as user props from the backend?

  return {
    completedSubtitle: t('landing-page:stepsToComplete.completedSubtitle'),
    completedTitle: t('landing-page:stepsToComplete.completedTitle'),
    description: t('landing-page:stepsToComplete.description'),
    onDismiss: () => {
      setCompleteProfilePromptCompleted(true);
    },
    settingsPromptState,
    steps: [
      {
        id: 'add-listing',
        icon: '📝',
        label: t('landing-page:stepsToComplete.addListings', { count: Math.min(user?.offersCount || 0, 5) }),
        completed: !!user?.offersCount && user.offersCount >= 5,
        onPress: () => {
          if (user?.offersCount && user.offersCount >= 5) return;
          actions.openPublishFlow();
        },
      },
      {
        id: 'add-picture',
        icon: '📷',
        label: t('landing-page:stepsToComplete.addPicture'),
        completed: hasCustomProfilePicture,
        onPress: () => {
          if (hasCustomProfilePicture || isPending || isPickingImage) return;
          onUseImagePickerCamera();
        },
      },
      {
        id: 'add-bio',
        icon: '✏️',
        label: t('landing-page:stepsToComplete.addBio'),
        completed: !!profile?.bio,
        onPress: () => {
          if (profile?.bio) return;
          actions.openMyInformation();
        },
      },
      {
        id: 'add-interest',
        icon: '🧡',
        label: t('landing-page:stepsToComplete.setInterests'),
        completed: !!userInterests && userInterests.length > 0,
        onPress: () => {
          if (userInterests && userInterests.length > 0) return;
          actions.openSharerInterests();
        },
      },
    ].filter((step) => canUseInterests() || step.id !== 'add-interest'),
    title: t('landing-page:stepsToComplete.title'),
  };
}

export function ProfileCompletionSteps({
  settingsPromptState,
  ...stepsProps
}: ReturnType<typeof useProfileCompletionSteps>) {
  return (
    <>
      <StepsToComplete {...stepsProps} />
      <OpenSettingsPrompt {...settingsPromptState} settingName="camera" />
    </>
  );
}

function LandingProfileLocation({
  addressLabel,
  hasPaidSubscription,
  isLarge,
}: {
  addressLabel: string;
  hasPaidSubscription: boolean | undefined;
  isLarge: boolean;
}) {
  if (hasPaidSubscription === undefined && !addressLabel) return null;
  return (
    <Row alignItems="center" gap="spacing.general.x1" style={isLarge ? undefined : styles.smallAddressRow}>
      {hasPaidSubscription === undefined ? null : (
        <PartagePlusBadge plan={hasPaidSubscription ? 'premium' : 'free'} variant="small" />
      )}
      {hasPaidSubscription !== undefined && addressLabel ? (
        <Text color="text.secondary" variant="bodyBase">
          ·
        </Text>
      ) : null}
      {addressLabel ? (
        <Text color="text.secondary" fontSize={isLarge ? 13 : 12}>
          {addressLabel}
        </Text>
      ) : null}
    </Row>
  );
}

function LandingProfileDetails({
  addressLabel,
  hasPaidSubscription,
  greeting,
  isLarge,
  profile,
}: {
  addressLabel: string;
  hasPaidSubscription: boolean | undefined;
  greeting: string;
  isLarge: boolean;
  profile: NonNullable<LandingProfile>;
}) {
  return (
    <Column flex={1} gap="spacing.general.x0,5">
      <Row alignItems="center" flexWrap="wrap" style={isLarge ? styles.greetingLine : undefined}>
        <Text fontSize={isLarge ? 28 : 22} fontWeight="700" lineHeight={isLarge ? 34 : 27} numberOfLines={1}>
          {`${greeting}${profile.firstName ? ` ${profile.firstName}` : ''}`}
        </Text>
      </Row>
      {hasPaidSubscription !== undefined || addressLabel ? (
        <LandingProfileLocation
          addressLabel={addressLabel}
          hasPaidSubscription={hasPaidSubscription}
          isLarge={isLarge}
        />
      ) : null}
    </Column>
  );
}

export function LandingProfileSummary({
  addressLabel,
  greeting,
  navigation,
  profile,
  user,
  variant,
}: {
  addressLabel: string;
  greeting: string;
  navigation: LandingNavigation;
  profile: LandingProfile;
  user: LandingUser;
  variant: 'small' | 'large';
}) {
  const isLarge = variant === 'large';
  const isAmbassador = useAmbassador().isAmbassador;
  const hasPaidSubscription = useHasPartagePlusSubscription();
  const showSuperPartageur = Boolean(profile?.isSuper) && !isWhiteLabel(WhiteLabel.HauteGaspesie);

  if (!profile) {
    return <GreetingSkeleton />;
  }

  const badge = (
    <UserAvatarBadge
      userId={user?.id}
      avatarSize={60}
      size={40}
      isAmbassador={isAmbassador}
      isSuper={showSuperPartageur}
    />
  );
  const openProfile = () => {
    if (user?.id) navigateToUserProfile(navigation, user.id);
  };

  return (
    <Pressable disabled={!user?.id} onPress={openProfile}>
      <Row gap="spacing.general.x3" alignItems="center">
        <Column style={styles.avatarRing}>
          <Thumbnail height={60} source={{ uri: profile.picture }} useOwnCache style={styles.avatar} badge={badge} />
        </Column>
        <LandingProfileDetails
          addressLabel={addressLabel}
          hasPaidSubscription={hasPaidSubscription}
          greeting={greeting}
          isLarge={isLarge}
          profile={profile}
        />
      </Row>
    </Pressable>
  );
}

interface LandingQuickActionsProps {
  activitySubtitle: string;
  isLoggedIn: boolean;
  statsLoaded: boolean;
  unreadCount: number;
  hasLateLoans: boolean;
  actions: ReturnType<typeof useLandingPageNavigationActions>;
  variant: 'small' | 'large';
}

function useQuickActionsModel({ actions, variant }: Pick<LandingQuickActionsProps, 'actions' | 'variant'>) {
  const userInfo = useAtomValue(userInfoAtom);
  const { canUseSocialFeed } = useSocialFeedFeature();
  const [quickActionHeight, setQuickActionHeight] = useState<number | undefined>();
  const streakEnabled = isFeatureEnabled(FeatureFlag.streak, userInfo?.featureFlags);
  const equalizeQuickActionHeights = variant === 'large';
  const trackStreakFlameClick = useTrackStreakFlameClick();
  const isMonthlyStreak = userInfo?.hasShared === true;
  const monthlyStatsQuery = useMonthlyStatsAll({ enabled: streakEnabled && isMonthlyStreak });
  const lendingPastLoansQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Lending);
  const borrowingPastLoansQuery = useAllLoans(LoanFilterKey.PastLoans, LoanType.Borrowing);
  const loansWithoutPosts = getLoansWithoutPosts(borrowingPastLoansQuery.data ?? []);
  const { showNewPostPrompt } = useNewListingPrompt();
  const pendingLendingRatingsCount =
    lendingPastLoansQuery.data?.filter((loan) => isLoanPendingCurrentUserRating(loan, userInfo?.id)).length ?? 0;
  const pendingBorrowingRatingsCount =
    borrowingPastLoansQuery.data?.filter((loan) => isLoanPendingCurrentUserRating(loan, userInfo?.id)).length ?? 0;
  const pendingRatingsCount = pendingLendingRatingsCount + pendingBorrowingRatingsCount;
  const pendingRatingsInitialType = pendingLendingRatingsCount > 0 ? LoanType.Lending : LoanType.Borrowing;
  const streakMonthlyStatus = monthlyStatsQuery.data?.[0] ? dtoToStatus(monthlyStatsQuery.data[0]) : null;
  const openStreak = () => {
    trackStreakFlameClick();
    actions.openStreak();
  };
  const registerQuickActionHeight = (height: number) => {
    const normalizedHeight = Math.min(Math.ceil(height), QUICK_ACTION_CARD_MAX_HEIGHT);
    setQuickActionHeight((current) =>
      current !== undefined && current >= normalizedHeight ? current : normalizedHeight,
    );
  };
  const insets = useSafeAreaInsets();
  const horizontalPadding = insets.left || theme.spacing['spacing.general.x4'];

  return {
    canUseSocialFeed,
    dailyCurrentStreak: userInfo?.numStreak ?? 0,
    dailyStreakFrozen: (userInfo?.streakMissedDaySlots ?? []).includes((userInfo?.streakCurrentDaySlot ?? 1) - 1),
    equalizeQuickActionHeights,
    horizontalPadding,
    isMonthlyStreak,
    monthlyStreakFrozen: monthlyStatsQuery.data?.[0]?.isFreeze === true,
    loansWithoutPostsCount: loansWithoutPosts.length,
    onMeasuredHeight: equalizeQuickActionHeights ? registerQuickActionHeight : undefined,
    openStreak,
    pendingRatingsCount,
    pendingRatingsInitialType,
    quickActionHeight: equalizeQuickActionHeights ? quickActionHeight : undefined,
    showNewPostPrompt,
    streakEnabled,
    streakMonthlyStatus,
    userInfo,
  };
}

type QuickActionsModel = ReturnType<typeof useQuickActionsModel>;
type QuickActionCardContext = Pick<QuickActionsModel, 'onMeasuredHeight' | 'quickActionHeight'>;

function createActivityCard({
  actions,
  activitySubtitle,
  context,
  hasLateLoans,
  t,
}: {
  actions: LandingQuickActionsProps['actions'];
  activitySubtitle: string;
  context: QuickActionCardContext;
  hasLateLoans: boolean;
  t: TFunction<'landing-page'>;
}): QuickActionElement {
  return (
    <QuickActionCard
      key="activity"
      icon={hasLateLoans ? '🚨' : '📊'}
      title={t('landing-page:quickActions.activity')}
      subtitle={activitySubtitle}
      height={context.quickActionHeight}
      onMeasuredHeight={context.onMeasuredHeight}
      onPress={actions.openManagementOverview}
    />
  );
}

function createMessagesCard({
  actions,
  context,
  t,
  unreadCount,
}: {
  actions: LandingQuickActionsProps['actions'];
  context: QuickActionCardContext;
  t: TFunction<'landing-page'>;
  unreadCount: number;
}): QuickActionElement {
  const hasUnreadMessages = unreadCount > 0;
  return (
    <QuickActionCard
      key="messages"
      testID="landing-messages-quick-action"
      icon="💬"
      title={t('landing-page:quickActions.messages')}
      suffix={hasUnreadMessages ? <UnreadIndicator /> : ''}
      subtitle={
        hasUnreadMessages
          ? `${String(unreadCount)} ${t('landing-page:quickActions.unread')}`
          : t('landing-page:quickActions.noUnread')
      }
      height={context.quickActionHeight}
      onMeasuredHeight={context.onMeasuredHeight}
      onPress={actions.openMessages}
    />
  );
}

function createPendingRatingsCard({
  actions,
  context,
  pendingRatingsCount,
  pendingRatingsInitialType,
  t,
}: {
  actions: LandingQuickActionsProps['actions'];
  context: QuickActionCardContext;
  pendingRatingsCount: number;
  pendingRatingsInitialType: LoanType;
  t: TFunction<'landing-page'>;
}): QuickActionElement | null {
  if (pendingRatingsCount <= 0) return null;
  return (
    <QuickActionCard
      key="pending-ratings"
      icon="👍"
      title={t('landing-page:quickActions.pendingRatings')}
      subtitle={t('landing-page:quickActions.pendingRatingsSubtitle', { count: pendingRatingsCount })}
      height={context.quickActionHeight}
      onMeasuredHeight={context.onMeasuredHeight}
      onPress={() => actions.openPendingRatings(pendingRatingsInitialType)}
    />
  );
}

function createStreakCard({
  context,
  model,
  t,
}: {
  context: QuickActionCardContext;
  model: QuickActionsModel;
  t: TFunction<'landing-page'>;
}): QuickActionElement | null {
  if (!model.streakEnabled) return null;
  if (model.isMonthlyStreak) {
    return (
      <StreakQuickActionCard
        key="streak"
        title={t('profile-overview:streakHood')}
        confirmedTotal={model.streakMonthlyStatus?.confirmedTotal ?? 0}
        target={model.streakMonthlyStatus?.targetM ?? 0}
        sharesLabel={t('streak:shares')}
        contributionLabel={t('streak:myContribution', {
          count: model.streakMonthlyStatus?.individualConfirmedTotal ?? 0,
          defaultValue: 'Ma contribution',
        })}
        isFrozen={model.monthlyStreakFrozen}
        height={context.quickActionHeight}
        onMeasuredHeight={context.onMeasuredHeight}
        onPress={model.openStreak}
      />
    );
  }
  return (
    <DailyStreakQuickActionCard
      key="streak"
      currentStreak={model.dailyCurrentStreak}
      currentStreakLabel={t('streak:currentStreakLabel')}
      isFrozen={model.dailyStreakFrozen}
      title={t('streak:dailyContributions')}
      height={context.quickActionHeight}
      onMeasuredHeight={context.onMeasuredHeight}
      onPress={model.openStreak}
    />
  );
}

function createPostCard({
  context,
  model,
  t,
}: {
  context: QuickActionCardContext;
  model: QuickActionsModel;
  t: TFunction<'landing-page'>;
}): QuickActionElement | null {
  if (!model.canUseSocialFeed || model.loansWithoutPostsCount === 0) return null;
  return (
    <QuickActionCard
      key="create-post"
      testID="landing-create-post-quick-action"
      icon="✍️"
      title={t('landing-page:quickActions.createPost')}
      subtitle={t('landing-page:quickActions.createPostSubtitle', { count: model.loansWithoutPostsCount })}
      height={context.quickActionHeight}
      onMeasuredHeight={context.onMeasuredHeight}
      onPress={model.showNewPostPrompt}
    />
  );
}

function getOrderedQuickActionCards({
  activityCard,
  createPostCard: postCard,
  hasLateLoans,
  messagesCard,
  pendingRatingsCard,
  streakCard,
}: {
  activityCard: QuickActionElement;
  createPostCard: QuickActionElement | null;
  hasLateLoans: boolean;
  messagesCard: QuickActionElement;
  pendingRatingsCard: QuickActionElement | null;
  streakCard: QuickActionElement | null;
}) {
  const priorityCards = hasLateLoans
    ? [activityCard, streakCard, pendingRatingsCard]
    : [streakCard, pendingRatingsCard, activityCard];
  return [...priorityCards, messagesCard, postCard].filter((card): card is QuickActionElement => card !== null);
}

function QuickActionsLoading({ variant }: Pick<LandingQuickActionsProps, 'variant'>) {
  if (variant !== 'large') return <QuickActionsSkeleton />;
  return (
    <Row gap="spacing.general.x4" style={styles.largeQuickActions}>
      <QuickActionsSkeleton />
    </Row>
  );
}

function QuickActionsContent({
  cards,
  horizontalPadding,
  variant,
}: {
  cards: QuickActionElement[];
  horizontalPadding: number;
  variant: LandingQuickActionsProps['variant'];
}) {
  if (variant === 'large') {
    return (
      <Row gap="spacing.general.x4" style={styles.largeQuickActions}>
        <Row gap="spacing.general.x2" alignItems="center" flex={1} justifyContent="flex-end">
          {cards.map((card) =>
            React.cloneElement(card, {
              style: [styles.largeQuickActionCard, card.props.style],
            }),
          )}
        </Row>
      </Row>
    );
  }
  return (
    <FlatList
      data={cards}
      horizontal
      keyExtractor={(card) => String(card.key)}
      renderItem={({ item: card }) =>
        React.cloneElement(card, {
          style: [styles.smallQuickActionCard, card.props.style],
        })
      }
      showsHorizontalScrollIndicator={false}
      style={{ marginHorizontal: -horizontalPadding, paddingHorizontal: horizontalPadding }}
      contentContainerStyle={styles.smallQuickActionsContent}
    />
  );
}

export function LandingQuickActions(props: LandingQuickActionsProps) {
  const { activitySubtitle, isLoggedIn, statsLoaded, unreadCount, actions, hasLateLoans, variant } = props;
  const { t } = useTranslation('landing-page');
  const model = useQuickActionsModel({ actions, variant });

  if (!isLoggedIn) {
    return null;
  }
  if (!statsLoaded || !model.userInfo) return <QuickActionsLoading variant={variant} />;

  const context = {
    onMeasuredHeight: model.onMeasuredHeight,
    quickActionHeight: model.quickActionHeight,
  };
  const activityCard = createActivityCard({ actions, activitySubtitle, context, hasLateLoans, t });
  const messagesCard = createMessagesCard({ actions, context, t, unreadCount });
  const pendingRatingsCard = createPendingRatingsCard({
    actions,
    context,
    pendingRatingsCount: model.pendingRatingsCount,
    pendingRatingsInitialType: model.pendingRatingsInitialType,
    t,
  });
  const streakCard = createStreakCard({ context, model, t });
  const postCard = createPostCard({ context, model, t });
  const cards = getOrderedQuickActionCards({
    activityCard,
    createPostCard: postCard,
    hasLateLoans,
    messagesCard,
    pendingRatingsCard,
    streakCard,
  });

  return <QuickActionsContent cards={cards} horizontalPadding={model.horizontalPadding} variant={variant} />;
}

function StreakQuickActionCard({
  title,
  confirmedTotal,
  target,
  sharesLabel,
  contributionLabel,
  isFrozen = false,
  height,
  onMeasuredHeight,
  onPress,
  style,
}: {
  title: string;
  confirmedTotal: number;
  target: number;
  sharesLabel: string;
  contributionLabel: string;
  isFrozen?: boolean;
  height?: number;
  onMeasuredHeight?: (height: number) => void;
  onPress: () => void;
  style?: React.ComponentProps<typeof QuickActionCard>['style'];
}) {
  const hasMeasured = React.useRef(false);

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        { flex: 1, minHeight: QUICK_ACTION_CARD_HEIGHT, height, alignSelf: 'flex-start' },
        { opacity: pressed ? 0.72 : 1 },
        style,
      ]}
    >
      <Row
        alignItems="center"
        justifyContent="space-between"
        onLayout={(event) => {
          const measuredHeight = event.nativeEvent.layout.height;
          if (onMeasuredHeight && !hasMeasured.current && measuredHeight > 0) {
            hasMeasured.current = true;
            onMeasuredHeight(measuredHeight);
          }
        }}
        style={[styles.streakQuickActionContent, height !== undefined ? styles.fullHeight : undefined]}
      >
        <Column flex={1} gap="spacing.general.x0,5" style={styles.streakTextColumn}>
          <Text fontSize={13} fontWeight="600" numberOfLines={1} style={styles.streakTitle}>
            {title}
          </Text>
          <Row alignItems="flex-end" gap="spacing.general.x0,5">
            <Text fontSize={20} fontWeight="700" numberOfLines={1} style={styles.streakCount}>
              {String(confirmedTotal)}
            </Text>
            <Text fontSize={12} color="text.secondary" numberOfLines={1} style={styles.streakShares}>
              /{String(target)} {sharesLabel}
            </Text>
          </Row>
          <Text fontSize={9} color="text.secondary" numberOfLines={1} style={styles.streakContribution}>
            {contributionLabel}
          </Text>
        </Column>
        <Column
          alignItems="center"
          justifyContent="center"
          style={[styles.streakIconBadge, isFrozen ? styles.streakIconBadgeFrozen : undefined]}
        >
          <Text fontSize={18} lineHeight={18 * 1.2}>
            {isFrozen ? '🧊' : '🔥'}
          </Text>
        </Column>
      </Row>
    </Pressable>
  );
}

function DailyStreakQuickActionCard({
  currentStreak,
  currentStreakLabel,
  isFrozen,
  title,
  height,
  onMeasuredHeight,
  onPress,
  style,
}: {
  currentStreak: number;
  currentStreakLabel: string;
  isFrozen: boolean;
  title: string;
  height?: number;
  onMeasuredHeight?: (height: number) => void;
  onPress: () => void;
  style?: React.ComponentProps<typeof QuickActionCard>['style'];
}) {
  const hasMeasured = React.useRef(false);

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        { flex: 1, minHeight: QUICK_ACTION_CARD_HEIGHT, height, alignSelf: 'flex-start' },
        { opacity: pressed ? 0.72 : 1 },
        style,
      ]}
    >
      <Row
        alignItems="center"
        justifyContent="space-between"
        onLayout={(event) => {
          const measuredHeight = event.nativeEvent.layout.height;
          if (onMeasuredHeight && !hasMeasured.current && measuredHeight > 0) {
            hasMeasured.current = true;
            onMeasuredHeight(measuredHeight);
          }
        }}
        style={[styles.streakQuickActionContent, height !== undefined ? styles.fullHeight : undefined]}
      >
        <Column flex={1} gap="spacing.general.x0,5" style={styles.streakTextColumn}>
          <Text fontSize={13} fontWeight="600" numberOfLines={1} style={styles.streakTitle}>
            {title}
          </Text>
          <Row alignItems="baseline" gap="spacing.general.x0,5">
            <Text fontSize={12} color="text.secondary" numberOfLines={1}>
              {currentStreakLabel}:
            </Text>
            <Text fontSize={16} fontWeight="700" numberOfLines={1} style={styles.streakCount}>
              {String(currentStreak)}
            </Text>
          </Row>
        </Column>
        <Column
          alignItems="center"
          justifyContent="center"
          style={[styles.streakIconBadge, isFrozen ? styles.streakIconBadgeFrozen : undefined]}
        >
          <Text fontSize={18} lineHeight={18 * 1.2}>
            {isFrozen ? '🧊' : '🔥'}
          </Text>
        </Column>
      </Row>
    </Pressable>
  );
}

function UnreadIndicator() {
  return (
    <Column
      alignItems="center"
      justifyContent="center"
      height={theme.spacing['spacing.general.x1']}
      width={theme.spacing['spacing.general.x1']}
      borderRadius="radius.xl"
      backgroundColor="icon.error"
    />
  );
}

const styles = StyleSheet.create({
  avatar: {
    borderRadius: theme.borderRadii['radius.full'],
  },
  avatarRing: {
    borderColor: theme.colors['surface.tertiary'],
    borderRadius: theme.borderRadii['radius.full'],
    borderWidth: 2,
    padding: 2,
  },
  greetingLine: {
    flexWrap: 'wrap',
  },
  largeQuickActions: {
    minWidth: 400,
  },
  largeQuickActionCard: {
    flex: 1,
    minWidth: 180,
  },
  largeSuperBadge: {
    bottom: -theme.spacing['spacing.general.x2'],
    height: 32,
    position: 'absolute',
    right: -theme.spacing['spacing.general.x1'],
    width: 32,
  },
  smallAddressRow: {
    marginTop: theme.spacing['spacing.general.x0,5'],
  },
  smallQuickActionCard: {
    flex: 0,
    // CSS flex: 0 uses a zero basis, collapsing cards inside the horizontal web list.
    flexBasis: 'auto',
    flexShrink: 0,
    width: 170,
  },
  smallQuickActionsContent: {
    alignItems: 'flex-start',
    gap: theme.spacing['spacing.general.x3'],
    paddingRight: theme.spacing['spacing.general.x4'],
  },
  fullHeight: {
    height: '100%',
  },
  streakContribution: {
    lineHeight: 11,
  },
  streakCount: {
    lineHeight: 22,
  },
  streakIconBadge: {
    position: 'absolute',
    right: theme.spacing['spacing.general.x2'],
    bottom: theme.spacing['spacing.general.x2'],
    backgroundColor: theme.colors['surface.brand.secondary-3.subtle'],
    borderRadius: 13,
    height: 28,
    marginLeft: theme.spacing['spacing.general.x2'],
    width: 28,
  },
  streakIconBadgeFrozen: {
    backgroundColor: theme.colors['surface.info.subtle'],
  },
  streakQuickActionContent: {
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['surface.inverse.tertiary-neutral'],
    borderRadius: theme.borderRadii['radius.lg'],
    borderWidth: 1,
    minHeight: 74,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 11,
    paddingTop: 9,
  },
  streakShares: {
    lineHeight: 17,
    marginBottom: 2,
  },
  streakTextColumn: {
    minWidth: 0,
  },
  streakTitle: {
    lineHeight: 16,
  },
  smallSuperBadge: {
    bottom: -theme.spacing['spacing.general.x2'],
    height: 30,
    position: 'absolute',
    right: -theme.spacing['spacing.general.x1'],
    width: 30,
  },
});
